import { queryClient } from './queryClient';
import { createLiveRefreshScheduler } from '../utils/liveRefreshScheduler';

// Global registry for manual refetch functions
const refetchRegistry = {
  batchShopify: null,
  bulkStatus: null,
};

// Page-specific registry to track which pages are active
const pageRegistry = {
  frontdesk: false,
};

export const registerRefetchFunction = (name, fn) => {
  refetchRegistry[name] = fn;
};

export const unregisterRefetchFunction = (name) => {
  refetchRegistry[name] = null;
};

export const registerPageActivity = (pageName) => {
  pageRegistry[pageName] = true;
};

export const unregisterPageActivity = (pageName) => {
  pageRegistry[pageName] = false;
};

export const invalidateByPayload = (eventType, payload) => {
  switch (eventType) {
    case 'treatment:updated':
    case 'treatment:created':
      if (payload.treatmentId) {
        queryClient.invalidateQueries({ queryKey: ['treatment', payload.treatmentId] });
      }
      if (payload.date) {
        queryClient.invalidateQueries({ queryKey: ['treatments', payload.date] });
      }
      break;

    case 'treatment:deleted':
      if (payload.treatmentId) {
        queryClient.removeQueries({ queryKey: ['treatment', payload.treatmentId] });
      }
      if (payload.date) {
        queryClient.invalidateQueries({ queryKey: ['treatments', payload.date] });
      }
      break;

    case 'shopify-order:updated':
    case 'shopify-order:created':
      if (payload.treatmentId) {
        queryClient.invalidateQueries({ queryKey: ['shopify-orders', payload.treatmentId] });
        queryClient.invalidateQueries({ queryKey: ['batch-status', payload.treatmentId] });
        queryClient.invalidateQueries({ queryKey: ['batch-order-validation', payload.treatmentId] });
        queryClient.invalidateQueries({ queryKey: ['bulk-status', payload.treatmentId] });
        queryClient.invalidateQueries({ queryKey: ['bulk-order-valid', payload.treatmentId] });
      }
      break;

    case 'shopify-order:deleted':
      if (payload.treatmentId) {
        queryClient.removeQueries({ queryKey: ['shopify-orders', payload.treatmentId] });
        queryClient.invalidateQueries({ queryKey: ['batch-status', payload.treatmentId] });
        queryClient.invalidateQueries({ queryKey: ['batch-order-validation', payload.treatmentId] });
        queryClient.invalidateQueries({ queryKey: ['bulk-status', payload.treatmentId] });
        queryClient.invalidateQueries({ queryKey: ['bulk-order-valid', payload.treatmentId] });
      }
      break;

    case 'patient-profile:updated':
      if (payload.patientId) {
        queryClient.invalidateQueries({ queryKey: ['patient-profile', payload.patientId] });
      }
      break;

    case 'customer-order:updated':
    case 'customer-order:created':
      if (payload.patientId) {
        queryClient.invalidateQueries({ queryKey: ['customer-orders', payload.patientId] });
      }
      break;

    case 'calendar-event:updated':
    case 'calendar-event:created':
      if (payload.eventId && payload.date) {
        queryClient.invalidateQueries({ queryKey: ['calendar-events', payload.eventId, payload.date] });
      }
      break;

    default:
      break;
  }
};

// Refresh events arrive per write (and again in the server's ~1.5 s batch). The
// scheduler collects them for a short random delay so a burst causes one round of
// refetches and open tabs don't all refetch at the same instant, and holds each target
// to a minimum gap so a continuous Shopify sync can't cause a refetch storm.
export const FRONTDESK_LIVE_REFRESH_GAP_MS = {
  ordered: 3000,
  bagprep: 3000,
  // Shopify order / payment status queries: the expensive ones.
  'ordered-payments': 10000,
  shopify: 10000,
};

// A treatment change also refreshes the FrontDesk payment queries (ordered-payments);
// those are throttled separately from the treatment list.
const targetsForSources = (sources) => sources.flatMap((source) => (
  source === 'ordered' ? ['ordered', 'ordered-payments'] : [source]
));

export const setupWebSocketCacheInvalidation = (wsClient, schedulerOptions = {}) => {
  const scheduler = createLiveRefreshScheduler({
    run: (targets) => { invalidateForSources(targets); },
    minGapMs: FRONTDESK_LIVE_REFRESH_GAP_MS,
    // The shopify target invalidates everything ordered-payments does.
    covers: { shopify: ['ordered-payments'] },
    ...schedulerOptions,
  });

  const handleRefresh = (data) => {
    if (!data || (data.type !== 'refresh' && data.type !== 'immediate-refresh')) return;
    const sources = data.sources || (data.source ? [data.source] : []);
    scheduler.schedule(targetsForSources(sources));
  };

  // Register the handler and return cleanup function
  const unsubscribe = wsClient.onRefresh(handleRefresh);
  return () => {
    scheduler.cancel();
    if (unsubscribe) unsubscribe();
  };
};

const invalidateForSources = (sources) => {
  try {
    sources.forEach(source => {
      switch (source) {
        case 'ordered':
          // Only process frontdesk-specific updates if frontdesk page is active
          if (pageRegistry.frontdesk) {
            queryClient.invalidateQueries({ queryKey: ['frontdesk-treatments'] });
            queryClient.invalidateQueries({ queryKey: ['frontdesk-orders'] });
            queryClient.invalidateQueries({ queryKey: ['bag-prep'] });
          }
          break;
        case 'ordered-payments':
          if (pageRegistry.frontdesk) {
            queryClient.invalidateQueries({ queryKey: ['shopify-orders'] });
            queryClient.invalidateQueries({ queryKey: ['batch-shopify-orders'] });
            queryClient.invalidateQueries({ queryKey: ['batch-status'] });
            queryClient.invalidateQueries({ queryKey: ['batch-order-validation'] });
            queryClient.invalidateQueries({ queryKey: ['bulk-status'] });
            queryClient.invalidateQueries({ queryKey: ['bulk-order-valid'] });

            // Manual refetches for non-React Query APIs
            if (refetchRegistry.batchShopify) {
              refetchRegistry.batchShopify();
            }

            if (refetchRegistry.bulkStatus) {
              refetchRegistry.bulkStatus();
            }
          }
          break;
        case 'shopify':
          queryClient.invalidateQueries({ queryKey: ['shopify-orders'] });
          queryClient.invalidateQueries({ queryKey: ['batch-shopify-orders'] });
          queryClient.invalidateQueries({ queryKey: ['batch-status'] });
          queryClient.invalidateQueries({ queryKey: ['batch-order-validation'] });
          queryClient.invalidateQueries({ queryKey: ['bulk-status'] });
          queryClient.invalidateQueries({ queryKey: ['bulk-order-valid'] });
          queryClient.invalidateQueries({ predicate: query =>
            query.queryKey[0]?.includes?.('shopify') ||
            query.queryKey[0]?.includes?.('batch') ||
            query.queryKey[0]?.includes?.('status') ||
            query.queryKey[0]?.includes?.('valid')
          });
          if (refetchRegistry.batchShopify) {
            refetchRegistry.batchShopify();
          }
          if (refetchRegistry.bulkStatus) {
            refetchRegistry.bulkStatus();
          }
          break;
        case 'bagprep':
          // Bag-prep check-offs only change iv_preps.
          if (pageRegistry.frontdesk) {
            queryClient.invalidateQueries({ queryKey: ['bag-prep'] });
          }
          break;
        case 'patientprofile':
        case 'patient-profile':
          queryClient.invalidateQueries({ queryKey: ['patient-profile'] });
          break;
        case 'calendars':
        case 'calendar':
          queryClient.invalidateQueries({ queryKey: ['calendars'] });
          queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
          break;
        case 'rooms':
          queryClient.invalidateQueries({ queryKey: ['room-options'] });
          break;
        default:
          break;
      }
    });
  } catch (error) {
    console.error('[WS-CACHE] Error handling refresh event:', error, sources);
  }
};

